const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

export interface DataGap {
  startPoint: [string, number]
  endPoint: [string, number]
  durationMs: number
}

/**
 * Returns the median interval (in ms) between consecutive data points.
 * Used as a baseline to distinguish real gaps from normal sparse sampling.
 */
export function getMedianInterval(data: [string, number][]): number {
  if (data.length < 2) return Infinity

  const intervals: number[] = []
  for (let i = 0; i < data.length - 1; i++) {
    const diff = new Date(data[i + 1][0]).getTime() - new Date(data[i][0]).getTime()
    if (diff > 0) intervals.push(diff)
  }
  if (intervals.length === 0) return Infinity

  intervals.sort((a, b) => a - b)
  return intervals[Math.floor(intervals.length / 2)]
}

/**
 * Detects gaps in time-series data.
 *
 * A gap is flagged only when the interval is BOTH:
 *   - longer than 15 minutes (rules out noise), AND
 *   - longer than 2× the median interval (rules out normally sparse data)
 */
export function detectDataGaps(data: [string, number][]): DataGap[] {
  const median = getMedianInterval(data)
  const threshold = Math.max(FIFTEEN_MINUTES_MS, median * 2)

  const gaps: DataGap[] = []
  for (let i = 0; i < data.length - 1; i++) {
    const currentTime = new Date(data[i][0]).getTime()
    const nextTime = new Date(data[i + 1][0]).getTime()
    const duration = nextTime - currentTime
    if (duration > threshold) {
      gaps.push({ startPoint: data[i], endPoint: data[i + 1], durationMs: duration })
    }
  }
  return gaps
}

/**
 * Inserts a null data point immediately after each gap's start timestamp.
 * This breaks the line in ECharts so it doesn't draw across the gap.
 */
export function insertNullsAtGaps(
  data: [string, number][],
  gaps: DataGap[]
): [string, number | null][] {
  if (gaps.length === 0) return data

  const gapStartTimestamps = new Set(gaps.map(g => g.startPoint[0]))
  const result: [string, number | null][] = []

  for (const point of data) {
    result.push(point)
    if (gapStartTimestamps.has(point[0])) {
      const nullTs = new Date(new Date(point[0]).getTime() + 1).toISOString()
      result.push([nullTs, null])
    }
  }
  return result
}

/**
 * Creates a dashed "bridge" series that visually spans each gap,
 * making it clear data was absent rather than simply not shown.
 */
import type { LineSeriesOption } from 'echarts'

export function createGapBridgeSeries(
  originalName: string | undefined,
  gaps: DataGap[],
  color: string
): LineSeriesOption | null {
  if (gaps.length === 0) return null

  const bridgeData: [string, number | null][] = []
  gaps.forEach((gap, i) => {
    if (i > 0) {
      const separatorTs = new Date(new Date(gaps[i - 1].endPoint[0]).getTime() + 1).toISOString()
      bridgeData.push([separatorTs, null])
    }
    bridgeData.push(gap.startPoint)
    bridgeData.push(gap.endPoint)
  })

  return {
    name: `${originalName ?? ''} (gap)`,
    data: bridgeData as LineSeriesOption['data'],
    type: 'line' as const,
    lineStyle: { color, type: 'dashed', width: 1.5, opacity: 0.5 },
    itemStyle: { color, opacity: 0 },
    showSymbol: false,
    connectNulls: false,
    silent: true,
    z: -1,
    animation: false,
  }
}
